import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';

/**
 * Props for the Card component.
 */
interface CardProps {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  collapsible?: boolean;
  initialCollapsed?: boolean;
  onCollapseToggle?: (collapsed: boolean) => void;
}

/**
 * Card component.
 * A container with a title and optional close button.
 * Used for dashboard panels and information displays.
 */
const Card: React.FC<CardProps> = ({ 
  title, 
  children, 
  onClose, 
  collapsible = false,
  initialCollapsed = false,
  onCollapseToggle
}) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    if (onCollapseToggle) {
      onCollapseToggle(newCollapsed);
    }
  };

  return (
    <CardContainer>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <ButtonContainer>
          {collapsible && (
            <CollapseButton onClick={handleToggleCollapse}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                {collapsed ? (
                  // Plus icon for collapsed state
                  <>
                    <path d="M8 4V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                ) : (
                  // Minus icon for expanded state
                  <path d="M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                )}
              </svg>
            </CollapseButton>
          )}
          {onClose && (
            <CloseButton onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CloseButton>
          )}
        </ButtonContainer>
      </CardHeader>
      {!collapsed && (
        <CardContent>
          {children}
        </CardContent>
      )}
    </CardContainer>
  );
};

/**
 * Styled container for the card.
 */
const CardContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  height: 100%;
`;

/**
 * Styled header for the card.
 */
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/**
 * Styled title for the card.
 */
const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

/**
 * Styled container for buttons.
 */
const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
`;

/**
 * Styled collapse button for the card.
 */
const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.radii.full};
  border: none;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundDark};
    color: ${({ theme }) => theme.colors.text};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

/**
 * Styled close button for the card.
 */
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.radii.full};
  border: none;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundDark};
    color: ${({ theme }) => theme.colors.text};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

/**
 * Styled content area for the card.
 */
const CardContent = styled.div`
  padding: ${({ theme }) => theme.space.md};
  flex-grow: 1;
  overflow: auto;
`;

export default Card; 